import React from "react";
import Facts from "components/Facts";

import { animations, previousTimeStamp, frameProcs } from "../animation";
import map from "../map";
import logLevels from "./logLevels";
import { effects } from "../render/effects";
import * as d3 from "d3";

const countsCache = {};

export const pendingEventStats = [];

const getEmoji = (data) => {
  if (!data) return "";
  const parts = data.split(":");
  if (parts.length < 2) return "";
  const emoji = parts[1];
  return emoji;
};

const innerRadius = 20;
const spinnerRadius = 10;
const outerRadius = innerRadius + spinnerRadius;

// The background and spinner groups of each log level, in level order — the pairs
// that `enter` builds inside a holder per level. Walked directly rather than
// queried from the document because under the WebGL renderer these are scene
// nodes, and because a global selector was being run for every counts event on
// every role.
function spinnerGroups(elem) {
  const groups = [];
  const children = elem.children;
  for (let i = 0; i < children.length; i++) {
    const holder = children[i];
    if (holder.tagName !== "g") continue;
    for (let j = 0; j < holder.children.length; j++) {
      if (holder.children[j].tagName === "g") groups.push(holder.children[j]);
    }
  }
  return groups;
}

const particles = false;

// Levels at or above these light the two plumes. Warnings smoke; errors and
// anything worse burn.
const SMOKE_FROM = 3;
const FIRE_FROM = 4;

// How many events in a single report count as a plume at full strength.
const PLUME_FULL = 6;

// Sets how hard each plume is going from the counts that just arrived.
//
// Quantised to fifths, because intensity is an attribute on a batch child and
// every distinct value it takes rebuilds the role's geometry. Reports arrive once
// a second, so without that a busy estate would rebuild every role every second
// for a change nobody could see.
function setPlumes(item, counts) {
  if (!item.smoke) return;

  let smoke = 0;
  let fire = 0;
  for (let i = SMOKE_FROM; i < counts.length; i++) {
    if (!(counts[i] > 0)) continue;
    if (i >= FIRE_FROM) fire += counts[i];
    else smoke += counts[i];
  }

  // Errors smoke as well as burn, so a role on fire is not a bare flame.
  setPlume(item, "smoke", (smoke + fire) / PLUME_FULL);
  setPlume(item, "fire", fire / PLUME_FULL);
}

// Carries a spinner past where it is going and lets it swing back, twice, dying
// away as it settles.
//
// A spinner is a disc, and a disc turning looks like a disc standing still — the
// only thing that reads as movement is the wedge arriving somewhere and rocking
// as it stops. Wound tightly enough to be seen at thirty frames a second, which
// is what a gentler spring would not be.
const OVERSHOOT_AMOUNT = 0.22;
const OVERSHOOT_CYCLES = 2.2;

function overshoot(t) {
  if (t >= 1 || !effects.motionStretch)
    return t >= 1 ? 1 : 1 - Math.pow(1 - t, 3);
  const eased = 1 - Math.pow(1 - t, 3);
  const decay = Math.pow(1 - t, 2);
  return (
    eased +
    Math.sin(t * Math.PI * 2 * OVERSHOOT_CYCLES) * OVERSHOOT_AMOUNT * decay
  );
}

// A role that stops erroring stops reporting altogether, so nothing would ever
// arrive to put its plume out. Both fade on their own, over a few seconds, and
// any report that does arrive overrides this on the way up.
//
// Registered on first use rather than at module scope: this file is reached
// through a cycle — animation imports the renderer, which reaches the schema,
// which lands back here — so `frameProcs` is still undefined while this module is
// being evaluated.
let plumesRegistered = false;

function registerPlumeDecay() {
  if (plumesRegistered) return;
  plumesRegistered = true;

  frameProcs.plumes = (time) => {
    const fade = Math.exp(-time / 2600);

    for (const key in map.objects) {
      const item = map.objects[key];
      if (!item.smoke) continue;
      if (item.smokeLevel > 0) setPlume(item, "smoke", item.smokeLevel * fade);
      if (item.fireLevel > 0) setPlume(item, "fire", item.fireLevel * fade);
    }
  };
}

// The level is kept exactly and only rounded on its way to the attribute.
//
// Rounding it into the stored value instead is what stopped the plumes fading at
// all: a step of a twentieth shrinks by well under half a step per frame, so it
// rounded back to itself every time and the level never moved. The plume then sat
// at full strength until the next report replaced it outright — which is the snap.
function setPlume(item, which, level) {
  const levelKey = which === "fire" ? "fireLevel" : "smokeLevel";
  const attrKey = which === "fire" ? "fireAttr" : "smokeAttr";

  item[levelKey] = Math.min(1, level);

  const value = Math.round(item[levelKey] * 20) / 20;
  if (value === item[attrKey]) return;

  item[attrKey] = value;
  item[which].attr("data-intensity", value);
}

export default {
  hasData: true,
  hasRag: true,
  collections: { ["*"]: { objectType: "scope" } },

  layout: { radius: innerRadius + spinnerRadius * 2 },
  render: {
    enter: (item) => {
      const { elem: e, data } = item;
      e.append("circle").attr("fill", "white").attr("r", innerRadius);

      e.append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("y", 3)
        .attr("style", "font-size: " + innerRadius * 1.4 + "px")
        .text(getEmoji(data));

      const midAngle = 90;
      const angleRange = 195;
      const fromAngle = midAngle + angleRange / 2;
      const toAngle = midAngle - angleRange / 2;
      const piOver180 = Math.PI / 180;
      const slice = (toAngle - fromAngle) / (logLevels.length - 1);

      for (let i = 0; i < logLevels.length; i++) {
        const ang = fromAngle + slice * i;
        const x = Math.cos(ang * piOver180) * outerRadius;
        const y = Math.sin(ang * piOver180) * outerRadius;

        const logLevel = logLevels[i];
        const holder = e
          .append("g")
          .attr("transform", "translate(" + x + "," + y + ")");
        holder.append("g");
        holder
          .append("g")
          .attr("data-props", `{"level":"${logLevel.name}"}`)
          .attr("transform", "rotate(-135)")
          .call((x) =>
            x.append("circle").attr("fill", "#fff").attr("r", spinnerRadius)
          )
          .append("path")
          .attr("fill", logLevel.colour)
          .attr(
            "d",
            `M0 0L${spinnerRadius} 0A${spinnerRadius} ${spinnerRadius} 0 0 1 0 ${spinnerRadius}Z`
          );
      }

      // What `effects.js` has always described: warnings smoke, errors burn.
      // Both plumes exist from the start and sit at zero intensity, because
      // creating them on demand would rebuild the role's geometry at exactly the
      // moment something is going wrong with it.
      item.smoke = e
        .append("particles")
        .attr("fill", "#8a8a8a")
        .attr("data-count", 26)
        .attr("data-size", 7)
        .attr("data-intensity", 0);
      item.fire = e
        .append("particles")
        .attr("fill", "#ff7418")
        .attr("data-count", 34)
        .attr("data-size", 5)
        .attr("data-intensity", 0);

      registerPlumeDecay();

      return e;
    },

    update: ({ elem: e, data }) => {
      e.select("text").text(getEmoji(data));
    },
  },

  eventHandlers: {
    counts: (item, n) => {
      const { id: o, elem: e } = item;
      var counts = n.split(",").map((x) => parseInt(x));
      var items = spinnerGroups(e);
      if (!(o in countsCache)) {
        countsCache[o] = [0, 0, 0, 0, 0, 0];
      }
      pendingEventStats.push(counts);

      const now = new Date().getTime();

      setPlumes(item, counts);

      for (var i = 0; i < counts.length; i++) {
        // Only when this level actually fired. Stamping every level on every
        // counts message — which is what this did — records "a message arrived"
        // rather than "this level fired", so `lastEvents` said Critical was
        // moments ago for every role in the estate. That is why every connection
        // line was drawn in the Critical colour, and it left nothing for the
        // idle filter to tell levels apart by.
        if (counts[i] > 0) {
          let itemPtr = item;
          while (itemPtr) {
            if (!itemPtr.lastEvents) itemPtr.lastEvents = {};
            itemPtr.lastEvents[logLevels[i].name] = now;
            if (!itemPtr.eventActivity) itemPtr.eventActivity = {};
            itemPtr.eventActivity[logLevels[i].name] =
              (itemPtr.eventActivity[logLevels[i].name] || 0) + counts[i];
            itemPtr = itemPtr.parent;
          }
        }

        const bgGrp = items[i * 2];
        const spinner = items[i * 2 + 1];
        if (!spinner) continue;
        const count = counts[i];

        if (particles) {
          const spurt = {
            sx: parseFloat(
              spinner.parentNode
                .getAttribute("transform")
                .split("(")[1]
                .split(",")[0]
            ),
            sy: parseFloat(
              spinner.parentNode
                .getAttribute("transform")
                .split("(")[1]
                .split(",")[1]
            ),
            r: spinnerRadius,
            c: logLevels[i].colour,
            ct: count,
            lf: 0,
            e: e,
          };

          animations.add({
            type: "spinny",
            start: previousTimeStamp,
            duration: 1000,
            from: 0,
            to: 1,
            frame: (v) => {
              const should = Math.floor(spurt.ct * v + 0.5);
              const did = Math.floor(spurt.ct * spurt.lf + 0.5);
              const n = should - did;
              for (var j = 0; j < n; j++) {
                const part = {
                  x: spurt.sx + Math.random() * spurt.r * 2 - spurt.r,
                  y: spurt.sy + Math.random() * spurt.r * 2 - spurt.r,
                  xv: Math.random() * 20 - 10,
                  yv: Math.random() * 20 - 15,
                  lt: 0,
                  e: spurt.e
                    .append("circle")
                    .attr("fill", spurt.c)
                    .attr("r", 3),
                };
                animations.add({
                  type: "fade-spinny",
                  start: previousTimeStamp,
                  duration: 2000,
                  from: 0,
                  to: 40,
                  frame: (v, done) => {
                    const dt = v - part.lt;
                    part.lt = v;
                    part.yv += dt;
                    part.x += part.xv * dt;
                    part.y += part.yv * dt;
                    part.e.attr("cx", part.x).attr("cy", part.y);
                    if (v > 30) {
                      part.e.attr("opacity", 1 - (v - 30) / 10);
                    }
                    if (done) {
                      part.e.remove();
                    }
                  },
                });
              }
              spurt.lf = v;
            },
          });
        }

        const start = -135 + countsCache[o][i] * 90;
        const travel = 90 * count;
        animations.add({
          type: "spin",
          start: previousTimeStamp,
          duration: 1000,
          from: 0,
          to: 1,
          frame: (t) => {
            spinner.setAttribute(
              "transform",
              "rotate(" + (start + travel * overshoot(t)) + ")"
            );
          },
        });
        switch ("DISABLED__" + logLevels[i].effect.type) {
          case "circleParticle":
            for (var j = 0; j < count; j++) {
              const timeOfs = (j / count) * 1000;
              const r1 = logLevels[i].effect.startRadius;
              const r2 = logLevels[i].effect.endRadius;
              const fill = logLevels[i].effect.colour;
              setTimeout(() => {
                const xofs = Math.random() * 16 - 8;
                const yofs = Math.random() * 16 - 8;
                const ht = Math.random() * 40 + 80;
                const flame = d3
                  .select(bgGrp)
                  .append("circle")
                  .attr("cx", xofs)
                  .attr("cy", yofs)
                  .attr("r", r1)
                  .attr("fill", fill);
                animations.add({
                  type: "particle-circle",
                  start: previousTimeStamp,
                  duration: 1000,
                  from: 0,
                  to: 1,
                  frame: (v, done) => {
                    if (done) {
                      flame.remove();
                      return;
                    }
                    flame.attr("opacity", 1 - v);
                    flame.attr("cy", yofs - v * ht);
                    flame.attr("r", r1 + (r2 - r1) * v);
                  },
                });
              }, timeOfs);
            }
            break;
          case "imageParticle":
            for (var j = 0; j < count; j++) {
              const timeOfs = (j / count) * 1000;
              const particleHref = logLevels[i].effect.particleHref;
              setTimeout(() => {
                const xofs = Math.random() * 10 - 5;
                const yofs = Math.random() * 10 - 5;
                const ht = Math.random() * 40 + 80;
                const flame = d3
                  .select(bgGrp)
                  .append("image")
                  .attr("x", -15 + xofs)
                  .attr("y", -15 + yofs)
                  .attr("width", 30)
                  .attr("height", 30)
                  .attr("href", particleHref);
                animations.add({
                  type: "particle-image",
                  start: previousTimeStamp,
                  duration: 1000,
                  from: 0,
                  to: 1,
                  frame: (v, done) => {
                    if (done) {
                      flame.remove();
                      return;
                    }
                    flame.attr("opacity", 1 - v);
                    flame.attr("y", -15 + yofs - v * ht);
                  },
                });
              }, timeOfs);
            }
            break;
        }

        countsCache[o][i] += count;
      }
    },
  },

  renderInfoHead: (t) => {
    if (!t.data) return t.label;
    const parts = t.data.split(":");
    const emoji = parts.length > 1 ? parts[1] : null;
    return (emoji ? emoji + " " : "") + t.label;
  },
  renderInfoBody: (t) => {
    if (!t.data) {
      return (
        <div>
          <h1>{t.label}</h1>
          <p>No data yet</p>
        </div>
      );
    }

    const parts = t.data.split(":");
    const shortName = parts[0];
    const description = parts.length > 2 ? parts[2] : null;

    return (
      <Facts>
        <Facts.Fact label="Abbreviation" value={shortName} />
        <Facts.Fact label="Description" value={description} />
      </Facts>
    );
  },
};
